import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import AOS from "aos";
import "aos/dist/aos.css";
import { FiPieChart, FiBarChart2, FiClipboard, FiCheck, FiArrowRight } from "react-icons/fi";
import { FaChartLine } from 'react-icons/fa';

const AIModelingPage = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [animationTriggered, setAnimationTriggered] = useState(false);

  useEffect(() => {
    // AOS 애니메이션 라이브러리 초기화
    AOS.init({
      duration: 800,
      once: true, // 한 번만 애니메이션 실행되도록 설정
      offset: 100,
      easing: 'ease-out-cubic'
    });
    
    // 스크롤 이벤트 리스너
    const handleScroll = () => {
      const position = window.pageYOffset;
      setScrollPosition(position);

      // 통계 섹션 확인
      if (statsRef.current) {
        const statsTop = statsRef.current.getBoundingClientRect().top;
        if (statsTop < window.innerHeight * 0.75 && !statsVisible) {
          setStatsVisible(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [statsVisible]);

  // 스크롤을 기반으로 헤더 스타일 변경
  const headerOpacity = Math.max(0, Math.min(1, 1 - scrollPosition / 500));
  
  // 메인 섹션으로 스크롤
  const scrollToMain = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <PageContainer>
      {/* 역동적인 헤더 섹션 */}
      <HeroSection ref={headerRef} style={{ opacity: headerOpacity }}>
        <HeroOverlay>
          <HeroContent>
            <HeroSuptitle data-aos="fade-down">G-VISION TECHNOLOGY</HeroSuptitle>
            <HeroTitle data-aos="fade-up" data-aos-delay="200">
              AI 모델링 <TitleHighlight>솔루션</TitleHighlight>
            </HeroTitle>
            <HeroDescription data-aos="fade-up" data-aos-delay="400">
              최신 AI 알고리즘으로 
              <br />맞춤형 데이터 분석 모델을 개발합니다.
            </HeroDescription>
            <HeroButtonsContainer data-aos="fade-up" data-aos-delay="600">
              <PrimaryButton>솔루션 소개서</PrimaryButton>
              <SecondaryButton>데모 신청</SecondaryButton>
            </HeroButtonsContainer>
          </HeroContent>
          <ScrollIndicator onClick={scrollToMain}>
            <ScrollIcon />
            <ScrollText>자세히 알아보기</ScrollText>
          </ScrollIndicator>
        </HeroOverlay>
        <HeroBackgroundContainer>
          <HeroBackground />
          <HeroVideoOverlay />
        </HeroBackgroundContainer>
      </HeroSection>

      {/* 핵심 통계 섹션 */}
      <StatsSection ref={statsRef}>
        <StatsContainer>
          <StatCard data-aos="zoom-in" data-aos-delay="0">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "40+" : "0"}
            </StatNumber>
            <StatName>산업 분석 모델</StatName>
            <StatDescription>다양한 산업군에 맞춤형 AI 모델링 솔루션 제공</StatDescription>
          </StatCard>
          
          <StatCard data-aos="zoom-in" data-aos-delay="100">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "97%" : "0%"}
            </StatNumber>
            <StatName>비즈니스 효율성</StatName>
            <StatDescription>AI 모델링 도입 후 기업 데이터 분석 효율성 향상률</StatDescription>
          </StatCard>
          
          <StatCard data-aos="zoom-in" data-aos-delay="200">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "60%" : "0%"}
            </StatNumber>
            <StatName>비용 절감</StatName>
            <StatDescription>데이터 기반 의사결정으로 인한 기업 평균 비용 절감률</StatDescription>
          </StatCard>
          
          <StatCard data-aos="zoom-in" data-aos-delay="300">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "45+" : "0"}
            </StatNumber>
            <StatName>고객사</StatName>
            <StatDescription>국내외 다양한 기업들이 선택한 AI 모델링 서비스</StatDescription>
          </StatCard>
        </StatsContainer>
      </StatsSection>

      {/* 소개 섹션 */}
      <IntroSection>
        <SectionContainer>
          <SectionHeading>
            <SectionTitleTag data-aos="fade-right">ABOUT</SectionTitleTag>
            <SectionTitle data-aos="fade-right" data-aos-delay="100">
              최적화된 <ColoredText>AI 모델링</ColoredText> 서비스
            </SectionTitle>
            <SectionSubtitle data-aos="fade-right" data-aos-delay="200">
              빅데이터를 비즈니스 인사이트로 전환하는 맞춤형 AI 모델링 솔루션
            </SectionSubtitle>
          </SectionHeading>

          <IntroContainer>
            <IntroImageColumn data-aos="fade-right">
              <IntroImageWrapper>
                <IntroImage src={require("../../aimg/logo_ai_modeling1.png")} alt="AI 모델링" />
                <IntroImageDecoration />
              </IntroImageWrapper>
            </IntroImageColumn>
            
            <IntroContentColumn data-aos="fade-left" data-aos-delay="200">
              <IntroParagraph>
                그래비젼의 AI 모델링 솔루션은 기업의 다양한 데이터를 활용하여 비즈니스 인텔리전스를 구현하는 맞춤형 서비스입니다. 고객 데이터, 시장 트렌드, 경영 지표 등 다양한 데이터를 통합 분석하고, 최적화된 AI 모델을 통해 의사결정을 지원하는 시스템을 제공합니다.
              </IntroParagraph>

              <IntroFeatureList>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiPieChart /></IntroFeatureIcon>
                  <IntroFeatureText>비즈니스 인텔리전스를 위한 맞춤형 분석 모델</IntroFeatureText>
                </IntroFeatureItem>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiBarChart2 /></IntroFeatureIcon>
                  <IntroFeatureText>직관적인 데이터 시각화 대시보드 제공</IntroFeatureText>
                </IntroFeatureItem>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FaChartLine /></IntroFeatureIcon>
                  <IntroFeatureText>실시간 데이터 분석과 알림 시스템</IntroFeatureText>
                </IntroFeatureItem>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiClipboard /></IntroFeatureIcon>
                  <IntroFeatureText>데이터 기반 의사결정 시스템</IntroFeatureText>
                </IntroFeatureItem>
              </IntroFeatureList>
              
              <LearnMoreButton>
                기술 문서 보기 <FiArrowRight />
              </LearnMoreButton>
            </IntroContentColumn>
          </IntroContainer>
        </SectionContainer>
      </IntroSection>

      {/* 핵심 기능 섹션 */}
      <FeaturesSection>
        <SectionContainer>
          <SectionHeading centered>
            <SectionTitleTag data-aos="fade-up">FEATURES</SectionTitleTag>
            <SectionTitle data-aos="fade-up">
              핵심 <ColoredText>기능</ColoredText>
            </SectionTitle>
            <SectionSubtitle data-aos="fade-up">
              그래비젼의 AI 모델링 솔루션이 제공하는 차별화된 기능
            </SectionSubtitle>
          </SectionHeading>

          <FeatureCardsSection>
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(0)}>
              <FeatureCardInner active={activeFeature === 0}>
                <FeatureCardIcon active={activeFeature === 0}>
                  <FiPieChart />
                </FeatureCardIcon>
                <FeatureCardTitle>비즈니스 인텔리전스</FeatureCardTitle>
                <FeatureCardDescription>
                  데이터 기반 의사결정 시스템
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
            
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(1)}>
              <FeatureCardInner active={activeFeature === 1}>
                <FeatureCardIcon active={activeFeature === 1}>
                  <FiBarChart2 />
                </FeatureCardIcon>
                <FeatureCardTitle>데이터 시각화</FeatureCardTitle>
                <FeatureCardDescription>
                  직관적인 대시보드 제공
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
            
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(2)}>
              <FeatureCardInner active={activeFeature === 2}>
                <FeatureCardIcon active={activeFeature === 2}>
                  <FaChartLine />
                </FeatureCardIcon>
                <FeatureCardTitle>실시간 분석</FeatureCardTitle>
                <FeatureCardDescription>
                  리얼타임 데이터 처리 시스템
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
            
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(3)}>
              <FeatureCardInner active={activeFeature === 3}>
                <FeatureCardIcon active={activeFeature === 3}>
                  <FiClipboard />
                </FeatureCardIcon>
                <FeatureCardTitle>의사결정 지원</FeatureCardTitle>
                <FeatureCardDescription>
                  인사이트 기반 추천 시스템
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
          </FeatureCardsSection>
          
          <FeatureDetailSection>
            {activeFeature === 0 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_business_intelligence.png")} alt="비즈니스 인텔리전스" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>비즈니스 인텔리전스</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      그래비젼의 비즈니스 인텔리전스 시스템은 기업 내외부의 다양한 데이터를 통합하고 분석하여 
                      경영 의사결정에 필요한 인사이트를 제공합니다. 과거 데이터 분석뿐 아니라 예측 모델링을 통해 
                      미래 트렌드를 파악하고 선제적 대응이 가능하도록 지원합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 통합 데이터 관리 플랫폼</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 고급 데이터 분석 엔진</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 맞춤형 보고서 생성 기능</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 트렌드 예측 및 시나리오 분석</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
            
            {activeFeature === 1 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_data_visualization.png")} alt="데이터 시각화" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>데이터 시각화 도구</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      복잡한 데이터를 직관적인 시각적 요소로 변환하여 이해하기 쉽게 전달합니다.
                      맞춤형 대시보드 구성, 다양한 차트 및 그래프 옵션, 인터랙티브 시각화 기능을 통해
                      데이터에 숨겨진 패턴과 관계를 쉽게 파악할 수 있습니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 맞춤형 대시보드 구성</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 다양한 시각화 템플릿 제공</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 인터랙티브 차트 및 그래프</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 데이터 드릴다운 및 필터링 기능</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
            
            {activeFeature === 2 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_realtime_dashboard.png")} alt="실시간 분석" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>실시간 분석 대시보드</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      데이터가 생성되는 즉시 처리하고 분석하여 실시간으로 인사이트를 제공합니다.
                      스트리밍 데이터 처리 기술과 실시간 알림 시스템을 통해 중요한 변화나 이상 징후를
                      즉시 감지하고 대응할 수 있도록 지원합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 실시간 데이터 처리 엔진</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 실시간 모니터링 대시보드</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 즉각적인 알림 및 경고 시스템</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 시계열 분석 및 패턴 감지</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
            
            {activeFeature === 3 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_decision_support.png")} alt="의사결정 지원" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>의사결정 지원 시스템</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      데이터 분석 결과를 바탕으로 최적의 의사결정을 지원하는 시스템을 제공합니다.
                      AI 기반 추천 엔진이 여러 시나리오를 분석하고 최적의 선택지를 제안하며,
                      의사결정 과정의 투명성과 추적 가능성을 보장합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> AI 기반 추천 엔진</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 시나리오 분석 및 비교 도구</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 의사결정 기록 및 추적 시스템</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 협업적 의사결정 플랫폼</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
          </FeatureDetailSection>
        </SectionContainer>
      </FeaturesSection>

      {/* 기술 스펙 섹션 */}
      <TechSpecSection>
        <TechSpecBackground>
          <SectionContainer>
            <TechSpecContent data-aos="fade-up">
              <TechSpecLeft>
                <SectionHeading light>
                  <SectionTitleTag light>SPECIFICATIONS</SectionTitleTag>
                  <SectionTitle light>
                    기술 <ColoredText light>스펙</ColoredText>
                  </SectionTitle>
                  <SectionSubtitle light>
                    그래비젼 AI 모델링 솔루션의 세부 기술 사양
                  </SectionSubtitle>
                </SectionHeading>
                <TechSpecDescription>
                  그래비젼의 AI 모델링 솔루션은 최신 데이터 분석 기술과 머신러닝 알고리즘을 활용하여
                  기업의 데이터로부터 최대한의 가치를 창출합니다. 기업 환경에 맞춘 확장성 있는 아키텍처와
                  다양한 데이터 소스 연동 기능을 통해 통합 데이터 분석 환경을 제공합니다.
                </TechSpecDescription>
                <TechSpecDownloadButton>
                  기술 명세서 다운로드 <FiArrowRight />
                </TechSpecDownloadButton>
              </TechSpecLeft>
              
              <TechSpecRight>
                <TechSpecTable>
                  <TechSpecRow>
                    <TechSpecLabel>데이터 연동</TechSpecLabel>
                    <TechSpecValue>100+ 데이터 소스 연동 지원</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>분석 알고리즘</TechSpecLabel>
                    <TechSpecValue>머신러닝, 딥러닝, 통계 분석</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>데이터 처리 용량</TechSpecLabel>
                    <TechSpecValue>페타바이트 규모 데이터 처리</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>시각화 옵션</TechSpecLabel>
                    <TechSpecValue>50+ 차트 유형, 맞춤형 대시보드</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>배포 옵션</TechSpecLabel>
                    <TechSpecValue>클라우드, 온프레미스, 하이브리드</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>API 지원</TechSpecLabel>
                    <TechSpecValue>REST, GraphQL, WebSocket</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>보안 표준</TechSpecLabel>
                    <TechSpecValue>ISO 27001, GDPR, 접근 제어</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>확장성</TechSpecLabel>
                    <TechSpecValue>수평적/수직적 확장 아키텍처</TechSpecValue>
                  </TechSpecRow>
                </TechSpecTable>
              </TechSpecRight>
            </TechSpecContent>
          </SectionContainer>
        </TechSpecBackground>
      </TechSpecSection>

      {/* 구현 사례 섹션 */}
      <CaseStudySection>
        <SectionContainer>
          <SectionHeading centered>
            <SectionTitleTag data-aos="fade-up">CASE STUDIES</SectionTitleTag>
            <SectionTitle data-aos="fade-up" data-aos-delay="100">
              구현 <ColoredText>사례</ColoredText>
            </SectionTitle>
            <SectionSubtitle data-aos="fade-up" data-aos-delay="200">
              그래비젼 AI 모델링 솔루션을 도입한 고객 성공 사례
            </SectionSubtitle>
          </SectionHeading>
          
          <CaseStudyGrid>
            <CaseStudyCard 
              data-aos="fade-up"
              data-aos-delay="0"
              onMouseEnter={() => {
                if (!animationTriggered) {
                  setAnimationTriggered(true);
                }
              }}
            >
              <CaseStudyImageContainer>
                <CaseStudyImage src={require("../../aimg/case1.png")} alt="유통업 매출 최적화" />
                <CaseStudyCategory>유통</CaseStudyCategory>
              </CaseStudyImageContainer>
              <CaseStudyContent>
                <CaseStudyTitle>유통 기업 매출 최적화 모델</CaseStudyTitle>
                <CaseStudyDescription>
                  국내 대형 유통 기업에 매출 예측 및 최적화 모델을 도입하여
                  재고 관리 효율화와 판매 전략 개선을 통한 매출 증대 달성
                </CaseStudyDescription>
                <CaseStudyResults>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>23%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>매출 증가</CaseStudyResultLabel>
                  </CaseStudyResult>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>35%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>재고 비용 절감</CaseStudyResultLabel>
                  </CaseStudyResult>
                </CaseStudyResults>
              </CaseStudyContent>
            </CaseStudyCard>
            
            <CaseStudyCard data-aos="fade-up" data-aos-delay="100">
              <CaseStudyImageContainer>
                <CaseStudyImage src={require("../../aimg/case2.png")} alt="에너지 최적화" />
                <CaseStudyCategory>에너지</CaseStudyCategory>
              </CaseStudyImageContainer>
              <CaseStudyContent>
                <CaseStudyTitle>에너지 소비 최적화 시스템</CaseStudyTitle>
                <CaseStudyDescription>
                  대규모 에너지 기업에 AI 기반 소비 패턴 분석 및 최적화 모델을 구축하여
                  에너지 효율성 증대 및 운영 비용 절감 실현
                </CaseStudyDescription>
                <CaseStudyResults>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>18%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>에너지 절감</CaseStudyResultLabel>
                  </CaseStudyResult>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>42%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>비용 절감</CaseStudyResultLabel>
                  </CaseStudyResult>
                </CaseStudyResults>
              </CaseStudyContent>
            </CaseStudyCard>
            
            <CaseStudyCard data-aos="fade-up" data-aos-delay="200">
              <CaseStudyImageContainer>
                <CaseStudyImage src={require("../../aimg/case3.png")} alt="마케팅 캠페인 최적화" />
                <CaseStudyCategory>마케팅</CaseStudyCategory>
              </CaseStudyImageContainer>
              <CaseStudyContent>
                <CaseStudyTitle>마케팅 캠페인 최적화 모델</CaseStudyTitle>
                <CaseStudyDescription>
                  글로벌 소비재 기업에 고객 데이터 기반 마케팅 최적화 모델을 도입하여
                  타겟 마케팅 효율성 증대 및 캠페인 ROI 향상
                </CaseStudyDescription>
                <CaseStudyResults>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>3.5x</CaseStudyResultNumber>
                    <CaseStudyResultLabel>마케팅 ROI</CaseStudyResultLabel>
                  </CaseStudyResult>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>28%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>전환율 증가</CaseStudyResultLabel>
                  </CaseStudyResult>
                </CaseStudyResults>
              </CaseStudyContent>
            </CaseStudyCard>
          </CaseStudyGrid>
          
          <ViewMoreButtonContainer data-aos="fade-up">
            <ViewMoreButton>
              모든 사례 보기 <FiArrowRight />
            </ViewMoreButton>
          </ViewMoreButtonContainer>
        </SectionContainer>
      </CaseStudySection>

      {/* CTAs 섹션 */}
      <CTASection>
        <SectionContainer>
          <CTAContainer data-aos="fade-up">
            <CTAContent>
              <CTATitle>
                데이터 기반 <ColoredText>비즈니스 의사결정</ColoredText>이 필요하신가요?
              </CTATitle>
              <CTADescription>
                그래비젼의 AI 모델링 솔루션으로 기업의 데이터를 가치 있는 인사이트로 전환하세요.
                <br />전문가와 상담을 통해 귀사에 최적화된 AI 모델링 서비스를 설계해 드립니다.
              </CTADescription>
              <CTAButtonsContainer>
                <CTAPrimaryButton>데모 신청하기</CTAPrimaryButton>
                <CTASecondaryButton>문의하기</CTASecondaryButton>
              </CTAButtonsContainer>
            </CTAContent>
            <CTAImageContainer>
              <CTAImage src={require("../../aimg/cta_image.png")} alt="데모 신청" />
            </CTAImageContainer>
          </CTAContainer>
        </SectionContainer>
      </CTASection>

      {/* 추가 제품 정보 섹션 */}
      <RelatedSection>
        <SectionContainer>
          <SectionHeading centered>
            <SectionTitleTag data-aos="fade-up">RELATED SOLUTIONS</SectionTitleTag>
            <SectionTitle data-aos="fade-up" data-aos-delay="100">
              함께 살펴보면 좋은 <ColoredText>솔루션</ColoredText>
            </SectionTitle>
          </SectionHeading>
          
          <RelatedProductGrid>
            <RelatedProductCard data-aos="fade-up" data-aos-delay="0">
              <RelatedProductImage src={require("../../aimg/logo_bitcoin.png")} alt="거래소 엔진" />
              <RelatedProductTitle>거래소 엔진</RelatedProductTitle>
              <RelatedProductDescription>
                안정성과 확장성을 갖춘 강력한 금융거래 엔진 솔루션을 제공합니다.
              </RelatedProductDescription>
              <RelatedProductLink>
                자세히 보기 <FiArrowRight />
              </RelatedProductLink>
            </RelatedProductCard>
            
            <RelatedProductCard data-aos="fade-up" data-aos-delay="100">
              <RelatedProductImage src={require("../../aimg/logo_deepleaning1.png")} alt="딥러닝" />
              <RelatedProductTitle>딥러닝</RelatedProductTitle>
              <RelatedProductDescription>
                방대한 데이터를 분석하여 고도의 패턴 인식과 예측을 실현합니다.
              </RelatedProductDescription>
              <RelatedProductLink>
                자세히 보기 <FiArrowRight />
              </RelatedProductLink>
            </RelatedProductCard>
            
            <RelatedProductCard data-aos="fade-up" data-aos-delay="200">
              <RelatedProductImage src={require("../../aimg/logo_isms.png")} alt="ISMS 인증" />
              <RelatedProductTitle>ISMS 인증</RelatedProductTitle>
              <RelatedProductDescription>
                높은 수준의 정보보안 체계를 구축하고 인증받은 안전한 시스템을 제공합니다.
              </RelatedProductDescription>
              <RelatedProductLink>
                자세히 보기 <FiArrowRight />
              </RelatedProductLink>
            </RelatedProductCard>
          </RelatedProductGrid>
        </SectionContainer>
      </RelatedSection>
    </PageContainer>
  );
};

// 애니메이션 정의
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// const slideInRight = keyframes`
//   from { transform: translateX(50px); opacity: 0; }
//   to { transform: translateX(0); opacity: 1; }
// `;

// const slideInLeft = keyframes`
//   from { transform: translateX(-50px); opacity: 0; }
//   to { transform: translateX(0); opacity: 1; }
// `;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// const bounce = keyframes`
//   0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
//   40% { transform: translateY(-20px); }
//   60% { transform: translateY(-10px); }
// `;

const scrollDown = keyframes`
  0% { transform: translateY(0); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translateY(20px); opacity: 0; }
`;

// 스타일 컴포넌트
const PageContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  font-family: 'Noto Sans KR', sans-serif;
  padding-top: 80px; /* 네비게이션 바 높이 */
  
  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

// 헤더 섹션
const HeroSection = styled.section`
  width: 100%;
  height: 100vh;
  position: relative;
  min-height: 700px;
  
  @media (max-width: 768px) {
    min-height: 600px;
  }
`;

const HeroBackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${require("../../aimg/carousel3bg.png")}) no-repeat;
  background-size: cover;
  background-position: center;
  animation: ${pulse} 15s infinite ease-in-out;
`;

const HeroVideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(64, 72, 226, 0.8) 0%, rgba(0, 0, 0, 0.8) 100%);
`;

const HeroOverlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 0 20px;
  text-align: center;
  color: #ffffff;
`;

const HeroSuptitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 3px;
  margin-bottom: 20px;
  opacity: 0.9;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 30px;
  letter-spacing: -1px;
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const TitleHighlight = styled.span`
  color: #4048E2;
  display: inline-block;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 0;
    width: 100%;
    height: 10px;
    background-color: rgba(64, 72, 226, 0.3);
    z-index: -1;
    
    @media (max-width: 768px) {
      height: 6px;
      bottom: 3px;
    }
  }
`;

const HeroDescription = styled.p`
  font-size: 24px;
  font-weight: 300;
  margin-bottom: 40px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 30px;
  }
`;

const HeroButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const PrimaryButton = styled.button`
  background-color: #4048E2;
  color: white;
  font-size: 18px;
  font-weight: 500;
  padding: 15px 35px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(64, 72, 226, 0.4);
  
  &:hover {
    background-color: #3038C2;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(64, 72, 226, 0.6);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 30px;
    width: 100%;
    max-width: 250px;
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: white;
  font-size: 18px;
  font-weight: 500;
  padding: 15px 35px;
  border-radius: 30px;
  border: 2px solid white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 30px;
    width: 100%;
    max-width: 250px;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ScrollIcon = styled.div`
  width: 30px;
  height: 50px;
  border: 2px solid white;
  border-radius: 25px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 10px;
    left: 50%;
    width: 6px;
    height: 6px;
    background-color: white;
    border-radius: 50%;
    transform: translateX(-50%);
    animation: ${scrollDown} 2s infinite;
  }
`;

const ScrollText = styled.div`
  color: white;
  font-size: 14px;
  margin-top: 10px;
  font-weight: 300;
`;

// 통계 섹션
const StatsSection = styled.section`
  padding: 80px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const StatsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  padding: 0 20px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const StatNumber = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #4048E2;
  margin-bottom: 15px;
  transition: opacity 0.5s ease-out;
  
  ${props => props.visible && css`
    animation: ${fadeIn} 1s forwards;
  `}
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const StatName = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const StatDescription = styled.div`
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

// 소개 섹션
const IntroSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeading = styled.div`
  margin-bottom: 60px;
  text-align: ${props => props.centered ? 'center' : 'left'};
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionTitleTag = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.light ? 'rgba(255, 255, 255, 0.8)' : '#4048E2'};
  margin-bottom: 15px;
  letter-spacing: 2px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  color: ${props => props.light ? 'white' : '#333'};
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const ColoredText = styled.span`
  color: ${props => props.light ? 'white' : '#4048E2'};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 0;
    width: 100%;
    height: 10px;
    background-color: ${props => props.light ? 'rgba(255, 255, 255, 0.2)' : 'rgba(64, 72, 226, 0.2)'};
    z-index: -1;
    
    @media (max-width: 768px) {
      height: 6px;
      bottom: 3px;
    }
  }
`;

const SectionSubtitle = styled.p`
  font-size: 20px;
  font-weight: 300;
  color: ${props => props.light ? 'rgba(255, 255, 255, 0.8)' : '#666'};
  max-width: 600px;
  margin: ${props => props.centered ? '0 auto' : '0'};
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const IntroContainer = styled.div`
  display: flex;
  gap: 50px;
  align-items: center;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const IntroImageColumn = styled.div`
  flex: 1;
  
  @media (max-width: 1024px) {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const IntroImageWrapper = styled.div`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    border: 2px solid #4048E2;
    border-radius: 10px;
    z-index: -1;
  }
`;

const IntroImage = styled.img`
  width: 100%;
  border-radius: 10px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const IntroImageDecoration = styled.div`
  position: absolute;
  top: 20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: rgba(64, 72, 226, 0.1);
  border-radius: 50%;
  z-index: -1;
  animation: ${float} 6s infinite ease-in-out;
`;

const IntroContentColumn = styled.div`
  flex: 1;
`;

const IntroParagraph = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #444;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const IntroFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
`;

const IntroFeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const IntroFeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(64, 72, 226, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: #4048E2;
  font-size: 20px;
`;

const IntroFeatureText = styled.div`
  font-size: 16px;
  color: #444;
`;

const LearnMoreButton = styled.button`
  background: none;
  border: none;
  color: #4048E2;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
`;

// 기능 섹션
const FeaturesSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

// 카드 섹션
const FeatureCardsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  margin-bottom: 60px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FeatureCard = styled.div`
  cursor: pointer;
  transform: translateY(0);
  transition: transform 0.4s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const FeatureCardInner = styled.div`
  background-color: ${props => props.active ? '#4048E2' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 15px;
  padding: 35px 25px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: ${props => props.active 
    ? '0 15px 35px rgba(64, 72, 226, 0.4)' 
    : '0 10px 25px rgba(0, 0, 0, 0.05)'};
  transition: all 0.4s ease;
  
  &:hover {
    box-shadow: ${props => props.active 
      ? '0 20px 40px rgba(64, 72, 226, 0.5)' 
      : '0 15px 35px rgba(0, 0, 0, 0.1)'};
  }
  
  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const FeatureCardIcon = styled.div`
  font-size: 40px;
  margin-bottom: 20px;
  color: ${props => props.active ? 'white' : '#4048E2'};
  transition: all 0.3s ease;
  
  ${FeatureCardInner}:hover & {
    animation: ${pulse} 1s infinite;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 15px;
  }
`;

const FeatureCardTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 10px;
  }
`;

const FeatureCardDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const FeatureDetailSection = styled.div`
  position: relative;
  min-height: 500px;
  
  @media (max-width: 768px) {
    min-height: 400px;
  }
`;

const FeatureDetailPanel = styled.div`
  background-color: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.5s ease;
  
  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const FeatureDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FeatureDetailImage = styled.div`
  img {
    width: 100%;
    border-radius: 15px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureDetailContent = styled.div`
  
`;

const FeatureDetailTitle = styled.h3`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const FeatureDetailDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #555;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 20px;
  }
`;

const FeatureDetailList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FeatureDetailItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 18px;
  color: #444;
  
  svg {
    color: #4048E2;
    margin-right: 15px;
    flex-shrink: 0;
    font-size: 20px;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 12px;
    
    svg {
      font-size: 18px;
      margin-right: 10px;
    }
  }
`;

// 기술 스펙 섹션
const TechSpecSection = styled.section`
  padding: 100px 0;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const TechSpecBackground = styled.div`
  background: linear-gradient(135deg, #373B98 0%, #4048E2 100%);
  border-radius: 30px;
  padding: 80px 40px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    border-radius: 20px;
  }
`;

const TechSpecContent = styled.div`
  display: flex;
  gap: 60px;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const TechSpecLeft = styled.div`
  flex: 1;
`;

const TechSpecDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TechSpecDownloadButton = styled.button`
  background: white;
  color: #4048E2;
  border: none;
  border-radius: 30px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    
    svg {
      transform: translateX(5px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 14px;
  }
`;

const TechSpecRight = styled.div`
  flex: 1;
`;

const TechSpecTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  overflow: hidden;
`;

const TechSpecRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const TechSpecLabel = styled.td`
  padding: 20px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  width: 40%;
  
  @media (max-width: 768px) {
    padding: 15px;
    font-size: 14px;
  }
`;

const TechSpecValue = styled.td`
  padding: 20px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  
  @media (max-width: 768px) {
    padding: 15px;
    font-size: 14px;
  }
`;

// 구현 사례 섹션
const CaseStudySection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CaseStudyCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const CaseStudyImageContainer = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const CaseStudyImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${CaseStudyCard}:hover & {
    transform: scale(1.1);
  }
`;

const CaseStudyCategory = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #4048E2;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 15px;
  border-radius: 20px;
`;

const CaseStudyContent = styled.div`
  padding: 30px;
`;

const CaseStudyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CaseStudyDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CaseStudyResults = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const CaseStudyResult = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CaseStudyResultNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #4048E2;
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CaseStudyResultLabel = styled.div`
  font-size: 13px;
  color: #666;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const ViewMoreButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ViewMoreButton = styled.button`
  background: none;
  border: 2px solid #4048E2;
  color: #4048E2;
  font-size: 16px;
  font-weight: 600;
  padding: 12px 30px;
  border-radius: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: #4048E2;
    color: white;
    
    svg {
      transform: translateX(5px);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 25px;
  }
`;

// CTA 섹션
const CTASection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 60px;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const CTAContent = styled.div`
  flex: 1;
  padding: 60px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.3;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const CTADescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #555;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CTAButtonsContainer = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const CTAPrimaryButton = styled.button`
  background: #4048E2;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(64, 72, 226, 0.2);
  
  &:hover {
    background: #3038C2;
    transform: translateY(-3px);
    box-shadow: 0 15px 25px rgba(64, 72, 226, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 25px;
  }
`;

const CTASecondaryButton = styled.button`
  background: none;
  border: 2px solid #4048E2;
  color: #4048E2;
  border-radius: 30px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(64, 72, 226, 0.1);
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 25px;
  }
`;

const CTAImageContainer = styled.div`
  flex: 1;
  min-height: 400px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 1024px) {
    min-height: 300px;
  }
`;

const CTAImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// 관련 제품 섹션
const RelatedSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const RelatedProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedProductCard = styled.div`
  background: #f9f9f9;
  border-radius: 15px;
  padding: 30px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
  }
`;

const RelatedProductImage = styled.img`
  width: 160px;
  height: 160px;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 20px;
`;

const RelatedProductTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const RelatedProductDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const RelatedProductLink = styled.a`
  color: #4048E2;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export default AIModelingPage;